<template>
    <Teleport to="#games-grid-count">
        <div class="games-grid__count">{{ totalCount }}</div>
    </Teleport>
    <Teleport to="#games-grid-filters" defer>
        <GamesFilterDesktop v-if="showDesktopFilter" />
        <GamesFilterMobile v-else />
    </Teleport>

    <div v-show="showCards" class="games-grid__grid">
        <GamesCardGame
            v-for="(game, index) in loadedGames"
            :key="`game-${index}`"
            :game="game"
            :variant="index === 0 ? 'double' : 'default'"
        />
        <GamesCardGame
            v-show="isNextPageAvailable"
            v-for="index in filterParameters.pageSize - 1"
            :key="index"
            :loading="true"
            :data-index="index - 1"
            ref="skeletons"
            :variant="index === 0 ? 'double' : 'default'"
        />
    </div>
    <GamesEmpty v-if="!showCards" />
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useBreakpoints } from "@sazka/utils/composables/use-breakpoints";
import GamesFilterMobile from "./components/games-grid/games-filter-mobile.vue";
import GamesFilterDesktop from "./components/games-grid/games-filter-desktop.vue";
import GamesEmpty from "./components/games-grid/games-grid-empty.vue";
import GamesCardGame from "./components/games-card-game.vue";
import { useGamesGrid } from "./composables/use-games-grid";

const { loadedGames, totalCount, areGamesLoading, init, loadNextPage, filterParameters, isNextPageAvailable } =
    useGamesGrid();
const showCards = computed(() => loadedGames.value.length > 0 || isNextPageAvailable.value);
const showDesktopFilter = useBreakpoints("sm");
const skeletons = ref<InstanceType<typeof GamesCardGame>[]>([]);
const skeletonsInViewPort = ref<boolean[]>([]);
const isAnySkeletonInViewPort = computed(() => skeletonsInViewPort.value.some((s) => s));
const observer = ref<IntersectionObserver>();

watch([isAnySkeletonInViewPort, areGamesLoading], async ([isAnySkeletonInViewPortValue, areGamesLoadingValue]) => {
    if (isAnySkeletonInViewPortValue && !areGamesLoadingValue && isNextPageAvailable.value) {
        await loadNextPage();
    }
});

onMounted(async () => {
    observer.value = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                const index = entry.target.getAttribute("data-index");
                const temporarySkeletons = [...skeletonsInViewPort.value];
                temporarySkeletons[index] = entry.isIntersecting;
                skeletonsInViewPort.value = [...temporarySkeletons];
            });
        },
        { threshold: 0.1, rootMargin: "0px 0px 600px 0px" }
    );

    skeletons.value.forEach((element) => {
        observer.value.observe(element.rootElement);
    });

    await init();
});

onUnmounted(() => {
    observer.value?.disconnect();
});
</script>
